import { useState, useEffect } from 'react';
import { 
  ArrowLeft, Shield, Loader2, Key, Users, Plus, Trash2, 
  RefreshCw, CheckCircle2, AlertTriangle, XCircle
} from 'lucide-react';
import { useToast } from '@/lib/toast';
import { ssoApi } from '@/api/ssoApi';
import { integrationsApi, IntegrationStatusType, SyncHistoryEntry } from '@/api/integrationsApi';
import { useOrganizationHierarchy } from '@/lib/useOrganizationHierarchy';

const PREDEFINED_ROLES = [
  'Super Admin',
  'Admin',
  'Technician',
  'User',
  'SOC User',
  'SOC Technician',
  'Team Lead',
  'Department Head',
  'Approver',
  'Report Viewer'
];

export function SSOConfigurationPage({ onBack }: { onBack: () => void }) {
  const toast = useToast();
  const { departments, teams } = useOrganizationHierarchy();

  const [activeTab, setActiveTab] = useState<'status' | 'mappings' | 'history'>('status');

  // Azure Backend Status State
  const [azureStatus, setAzureStatus] = useState<any>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);

  // Group Mappings State
  const [mappings, setMappings] = useState<any[]>([]);
  const [newMapping, setNewMapping] = useState({
    provider: 'Microsoft Entra ID',
    sso_group_name: '',
    sso_group_id: '',
    role_name: 'Technician',
    department_id: '',
    team_id: '',
    status: 'Active'
  });
  const [isAddingMapping, setIsAddingMapping] = useState(false);

  // Sync History State
  const [syncHistory, setSyncHistory] = useState<SyncHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const loadStatusAndMappings = async () => {
    try {
      // 1. Fetch Azure backend status
      try {
        const statusRes = await integrationsApi.getIntegrationsStatus();
        if (statusRes?.azureSso) {
          setAzureStatus(statusRes.azureSso);
        }
      } catch (e) {
        console.error('Failed to load integration status', e);
      }

      // 2. Fetch Group Mappings
      try {
        const groupMaps = await ssoApi.getGroupMappings();
        if (groupMaps && groupMaps.length > 0) {
          setMappings(groupMaps);
        }
      } catch (e) {
        console.error('Failed to load group mappings', e);
      }
    } catch {
      // Fallback
    }
  };

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const hist = await integrationsApi.getAzureSyncHistory(25);
      setSyncHistory(hist);
    } catch (e) {
      console.error('Failed to load sync history', e);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadStatusAndMappings();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab]);

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setTestResult(null);
      const res = await integrationsApi.testAzureConnection();
      setTestResult(res);
      if (res.success) {
        toast.push(`Connected: ${res.message}`, 'success');
      } else {
        toast.push(`Connection Failed: ${res.message}`, 'error');
      }
      // Refresh status
      const updated = await integrationsApi.getIntegrationsStatus().catch(() => null);
      if (updated?.azureSso) setAzureStatus(updated.azureSso);
    } catch (err: any) {
      const msg = err.message || 'Connection test failed';
      setTestResult({ success: false, status: 'AUTHENTICATION_ERROR', message: msg });
      toast.push(msg, 'error');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleTriggerSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const res = await integrationsApi.syncAzureUsers();
      setSyncResult(res);
      if (res.success) {
        toast.push(`Sync Successful: Processed ${res.users_processed} users (${res.users_created} created, ${res.users_updated} updated, ${res.guest_users} guests).`, 'success');
      } else {
        toast.push(`Sync Warning: ${res.message}`, 'error');
      }
      loadStatusAndMappings();
      if (activeTab === 'history') loadHistory();
    } catch (err: any) {
      toast.push(`Sync Error: ${err.message}`, 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateMapping = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMapping.sso_group_name.trim()) return;

    try {
      await ssoApi.createGroupMapping({
        sso_group: newMapping.sso_group_id || newMapping.sso_group_name,
        sso_group_name: newMapping.sso_group_name,
        itsm_role: newMapping.role_name,
        department_id: newMapping.department_id || null,
        team_id: newMapping.team_id || null,
        status: newMapping.status
      });

      toast.push(`Group mapping created: ${newMapping.sso_group_name} -> ${newMapping.role_name}`, 'success');
      setNewMapping({
        provider: 'Microsoft Entra ID',
        sso_group_name: '',
        sso_group_id: '',
        role_name: 'Technician',
        department_id: '',
        team_id: '',
        status: 'Active'
      });
      setIsAddingMapping(false);
      const groupMaps = await ssoApi.getGroupMappings();
      if (groupMaps) setMappings(groupMaps);
    } catch (err: any) {
      toast.push(`Failed to save mapping: ${err.message}`, 'error');
    }
  };

  const handleDeleteMapping = async (id: string) => {
    try {
      await ssoApi.deleteGroupMapping(id);
      setMappings(prev => prev.filter(m => m.id !== id));
      toast.push('Group mapping deleted', 'info');
    } catch (err: any) {
      toast.push(`Failed to delete mapping: ${err.message}`, 'error');
    }
  };

  const status: IntegrationStatusType = azureStatus?.status || 'NOT_CONFIGURED';

  return (
    <div className="flex h-full flex-col bg-muted/10">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-500">
            <Key className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-lg font-semibold tracking-tight">Azure SSO & Microsoft Entra ID</h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              Backend-managed SSO authentication, automatic Member & Guest user sync, and role mappings.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleTestConnection}
            disabled={testingConnection}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2 text-xs font-semibold text-foreground hover:bg-muted transition shadow-sm disabled:opacity-50"
          >
            {testingConnection ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Test Connection
          </button>
          <button
            onClick={handleTriggerSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 transition shadow-sm disabled:opacity-50"
          >
            {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Users className="h-3.5 w-3.5" />}
            Sync Users Now
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 px-6 border-b border-border bg-card">
        <button
          onClick={() => setActiveTab('status')}
          className={`py-3 text-xs font-semibold border-b-2 transition ${
            activeTab === 'status' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Connection Status & Metrics
        </button>
        <button
          onClick={() => setActiveTab('mappings')}
          className={`py-3 text-xs font-semibold border-b-2 transition flex items-center gap-1.5 ${
            activeTab === 'mappings' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Azure Group Mappings
          <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold text-muted-foreground">{mappings.length}</span>
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`py-3 text-xs font-semibold border-b-2 transition flex items-center gap-1.5 ${
            activeTab === 'history' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Sync History
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 max-w-6xl w-full mx-auto space-y-6">
        {activeTab === 'status' && (
          <div className="space-y-6">
            {/* Safe Backend Notice */}
            <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-4 flex items-start gap-3">
              <Shield className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5" />
              <div className="text-xs text-muted-foreground leading-relaxed">
                <span className="font-semibold text-foreground">Backend-Only Secure Configuration: </span>
                Azure Client Secrets and Tenant IDs are loaded strictly from the server environment (<code className="font-mono text-indigo-400">backend/.env</code>).
                No credentials or private tokens are stored or exposed in the frontend. All user provisioning and group mapping occurs on the backend.
              </div>
            </div>

            {/* Connection Status Card */}
            <div className="rounded-2xl border border-border bg-card p-6 shadow-sm space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-6">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-bold text-foreground">Microsoft Entra ID (Azure AD)</h2>
                    {status === 'CONNECTED' ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-500 border border-emerald-500/20">
                        <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                        Connected
                      </span>
                    ) : status === 'CHECKING' ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/10 px-2.5 py-1 text-xs font-semibold text-sky-500 border border-sky-500/20">
                        <RefreshCw className="h-3 w-3 animate-spin" />
                        Checking...
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-500 border border-amber-500/20">
                        <AlertTriangle className="h-3 w-3" />
                        {status.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    OAuth 2.0 Client Credentials & Microsoft Graph v1.0 Directory Sync
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleTestConnection}
                    disabled={testingConnection}
                    className="px-3.5 py-2 rounded-xl bg-secondary text-secondary-foreground hover:bg-muted text-xs font-semibold transition flex items-center gap-2 border border-border"
                  >
                    {testingConnection ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    Test Connection
                  </button>
                  <button
                    onClick={handleTriggerSync}
                    disabled={syncing}
                    className="px-4 py-2 rounded-xl bg-primary text-primary-foreground hover:opacity-90 text-xs font-semibold transition flex items-center gap-2"
                  >
                    {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Users className="h-3.5 w-3.5" />}
                    Sync Now
                  </button>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-xl border border-border bg-muted/20 p-4">
                  <div className="text-xs font-medium text-muted-foreground">Total Users Synced</div>
                  <div className="text-2xl font-bold text-foreground mt-1">
                    {azureStatus?.user_count ?? '—'}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">All provisioned directory accounts</div>
                </div>

                <div className="rounded-xl border border-border bg-muted/20 p-4">
                  <div className="text-xs font-medium text-muted-foreground">Guest Users</div>
                  <div className="text-2xl font-bold text-indigo-500 mt-1">
                    {azureStatus?.guest_count ?? '—'}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">External Azure Entra ID Guests</div>
                </div>

                <div className="rounded-xl border border-border bg-muted/20 p-4">
                  <div className="text-xs font-medium text-muted-foreground">Last Successful Sync</div>
                  <div className="text-sm font-semibold text-foreground mt-2 truncate">
                    {azureStatus?.last_sync_at ? new Date(azureStatus.last_sync_at).toLocaleString() : 'Never'}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">Automated background sync</div>
                </div>
              </div>

              {/* Live Test / Sync Feedback */}
              {testResult && (
                <div className={`p-4 rounded-xl border text-xs ${
                  testResult.success ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                }`}>
                  <div className="font-semibold flex items-center gap-2">
                    {testResult.success ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    {testResult.message}
                  </div>
                  {testResult.latencyMs && (
                    <div className="text-[11px] text-muted-foreground mt-1">Latency: {testResult.latencyMs}ms</div>
                  )}
                </div>
              )}

              {syncResult && (
                <div className="p-4 rounded-xl border bg-primary/10 border-primary/20 text-xs text-foreground space-y-1">
                  <div className="font-semibold flex items-center gap-2 text-primary">
                    <CheckCircle2 className="h-4 w-4" />
                    {syncResult.message}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Created: {syncResult.users_created} • Updated: {syncResult.users_updated} • Guest Users: {syncResult.guest_users}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'mappings' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-foreground">Azure Security Group → ITSM Role Mappings</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Users matching these Azure groups will automatically be assigned their corresponding role, department, and team.
                </p>
              </div>
              <button
                onClick={() => setIsAddingMapping(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 transition shadow-sm"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Group Mapping
              </button>
            </div>

            {/* New Mapping Form */}
            {isAddingMapping && (
              <form onSubmit={handleCreateMapping} className="rounded-xl border border-border bg-card p-5 shadow-sm space-y-4">
                <h3 className="text-sm font-semibold text-foreground">New Azure Group Mapping</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-[11px] font-semibold text-muted-foreground uppercase">Azure Group Name / ID *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. IT-Support or SOC-Team"
                      value={newMapping.sso_group_name}
                      onChange={e => setNewMapping(prev => ({ ...prev, sso_group_name: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold text-muted-foreground uppercase">Target ITSM Role *</label>
                    <select
                      value={newMapping.role_name}
                      onChange={e => setNewMapping(prev => ({ ...prev, role_name: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-primary"
                    >
                      {PREDEFINED_ROLES.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold text-muted-foreground uppercase">Department (Optional)</label>
                    <select
                      value={newMapping.department_id}
                      onChange={e => setNewMapping(prev => ({ ...prev, department_id: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value="">None / Unassigned</option>
                      {departments.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold text-muted-foreground uppercase">Primary Team (Optional)</label>
                    <select
                      value={newMapping.team_id}
                      onChange={e => setNewMapping(prev => ({ ...prev, team_id: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value="">None / Unassigned</option>
                      {teams.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                  <button
                    type="button"
                    onClick={() => setIsAddingMapping(false)}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-border hover:bg-muted"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:opacity-90"
                  >
                    Save Mapping
                  </button>
                </div>
              </form>
            )}

            {/* Mappings Table */}
            <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
              <table className="w-full text-left text-xs">
                <thead className="bg-muted/50 text-muted-foreground border-b border-border">
                  <tr>
                    <th className="px-4 py-3 font-semibold uppercase">Azure Group</th>
                    <th className="px-4 py-3 font-semibold uppercase">ITSM Role</th>
                    <th className="px-4 py-3 font-semibold uppercase">Department</th>
                    <th className="px-4 py-3 font-semibold uppercase">Team</th>
                    <th className="px-4 py-3 font-semibold uppercase">Status</th>
                    <th className="px-4 py-3 text-right font-semibold uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {mappings.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                        No group mappings configured. Incoming users will receive the default role (USER).
                      </td>
                    </tr>
                  ) : (
                    mappings.map(m => {
                      const deptName = m.department?.name || departments.find(d => d.id === m.department_id)?.name || '—';
                      const teamName = m.team?.name || teams.find(t => t.id === m.team_id)?.name || '—';
                      const grpName = m.azure_group_name || m.sso_group || m.azure_group_id || 'Group';
                      const roleName = m.itsm_role || m.compass_role || m.role_name || 'User';

                      return (
                        <tr key={m.id} className="hover:bg-muted/30 transition">
                          <td className="px-4 py-3 font-semibold text-foreground">{grpName}</td>
                          <td className="px-4 py-3 font-medium text-primary">{roleName}</td>
                          <td className="px-4 py-3 text-muted-foreground">{deptName}</td>
                          <td className="px-4 py-3 text-muted-foreground">{teamName}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-500 border border-emerald-500/20">
                              Active
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleDeleteMapping(m.id)}
                              className="p-1 rounded text-muted-foreground hover:text-rose-500 transition"
                              title="Delete mapping"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-foreground">Azure SSO User Synchronization History</h2>
                <p className="text-xs text-muted-foreground mt-1">Audit log of all manual and scheduled synchronization runs.</p>
              </div>
              <button
                onClick={loadHistory}
                disabled={loadingHistory}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-semibold hover:bg-muted transition"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loadingHistory ? 'animate-spin' : ''}`} />
                Refresh History
              </button>
            </div>

            <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
              <table className="w-full text-left text-xs">
                <thead className="bg-muted/50 text-muted-foreground border-b border-border">
                  <tr>
                    <th className="px-4 py-3 font-semibold uppercase">Started At</th>
                    <th className="px-4 py-3 font-semibold uppercase">Status</th>
                    <th className="px-4 py-3 font-semibold uppercase">Processed</th>
                    <th className="px-4 py-3 font-semibold uppercase">Created</th>
                    <th className="px-4 py-3 font-semibold uppercase">Updated</th>
                    <th className="px-4 py-3 font-semibold uppercase">Guest Users</th>
                    <th className="px-4 py-3 font-semibold uppercase">Errors</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {syncHistory.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                        {loadingHistory ? 'Loading history...' : 'No synchronization history recorded yet.'}
                      </td>
                    </tr>
                  ) : (
                    syncHistory.map((h, i) => (
                      <tr key={h.id || i} className="hover:bg-muted/30 transition">
                        <td className="px-4 py-3 font-mono text-muted-foreground">
                          {new Date(h.started_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${
                            h.status === 'SUCCESS' ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-500 border border-rose-500/20'
                          }`}>
                            {h.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-semibold text-foreground">{h.users_processed || 0}</td>
                        <td className="px-4 py-3 text-emerald-500 font-semibold">{h.users_created || 0}</td>
                        <td className="px-4 py-3 text-sky-500">{h.users_updated || 0}</td>
                        <td className="px-4 py-3 text-indigo-400 font-semibold">{h.guest_users || 0}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {h.error_count > 0 ? (
                            <span className="text-rose-500 font-medium">{h.error_count} failed</span>
                          ) : (
                            '0'
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
